import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useContent } from "../lib/content";
import SEO from "../components/SEO";
import { buildWhatsAppLink, formatAfn, getProductPrice, getImageUrl } from "../lib/api";
import { MessageCircle, Check, Package, ArrowRight } from "lucide-react";
import ImageWithSkeleton from "../components/ImageWithSkeleton";

const categoryLabels = {
  honey: "عسل طبیعی",
  saffron: "زعفران هرات",
  dried_fruit: "میوه خشک",
  other: "سایر محصولات",
};

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { products, settings } = useContent();
  const [selectedImg, setSelectedImg] = useState(0);

  const product = useMemo(() => products.find((p) => p.slug === slug), [products, slug]);

  if (!product) {
    return (
      <section className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-stone-900 mb-4">محصول یافت نشد</h1>
        <p className="text-stone-600 mb-6">محصول مورد نظر در دسترس نیست.</p>
        <Link to="/products" className="inline-flex items-center gap-2 bg-amber-600 text-white font-semibold px-6 py-3 rounded-full">
          بازگشت به محصولات
        </Link>
      </section>
    );
  }

  const orderText = `سلام، می‌خواهم برای محصول «${product.name}» سفارش بدهم.`;

  return (
    <>
      <SEO title={`${product.name} | تحسین`} description={product.short_description} image={getImageUrl(product.images?.[0])} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="product-detail">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-6">
          <Link to="/" className="hover:text-amber-700">خانه</Link>
          <ArrowRight className="w-3 h-3" />
          <Link to="/products" className="hover:text-amber-700">محصولات</Link>
          <ArrowRight className="w-3 h-3" />
          <span className="text-stone-800">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="animate-fade-in">
            <div className="bg-white rounded-3xl overflow-hidden border border-stone-200 aspect-square relative">
              <ImageWithSkeleton
                src={getImageUrl(product.images?.[selectedImg]) || "https://images.unsplash.com/photo-1556316828-60a9fe5343ae?w=1000"}
                alt={product.name}
                className="w-full h-full object-cover"
                fetchPriority="high"
              />
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImg(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                      selectedImg === i ? "border-amber-600" : "border-stone-200"
                    }`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={getImageUrl(img, "thumb")} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="text-right animate-fade-up">
            <span className="inline-block text-xs font-semibold bg-amber-100 text-amber-800 px-3 py-1 rounded-full mb-4">
              {categoryLabels[product.category] || product.category}
            </span>
            <h1 className="text-3xl sm:text-4xl font-black text-stone-900 mb-3">{product.name}</h1>
            <p className="text-lg text-stone-600 leading-relaxed mb-6">{product.short_description}</p>

            <div className="bg-cream-100 rounded-3xl p-6 mb-6 border border-stone-200">
              <div className="flex items-baseline justify-between mb-4">
                <span className="text-sm text-stone-600">قیمت</span>
                {getProductPrice(product) ? (
                  <span className="text-2xl font-black text-amber-700 persian-numerals">{formatAfn(getProductPrice(product))}</span>
                ) : (
                  <span className="text-lg text-stone-700">برای اطلاع از قیمت تماس بگیرید</span>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-stone-700">
                {product.weight && (
                  <span className="inline-flex items-center gap-1 persian-numerals">
                    <Package className="w-4 h-4 text-amber-700" /> وزن: {product.weight}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-600" />
                  {product.in_stock ? "موجود" : "ناموجود"}
                </span>
              </div>
            </div>

            <a
              href={buildWhatsAppLink(settings.whatsapp_number, orderText)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold px-8 py-4 rounded-full text-lg shadow-md shadow-green-900/10 transition-colors"
              data-testid="product-whatsapp-order-btn"
            >
              <MessageCircle className="w-5 h-5" /> سفارش از طریق واتساپ
            </a>

            <div className="mt-8 prose-rtl">
              <h2 className="text-2xl font-bold text-stone-900 mb-3">توضیحات محصول</h2>
              {product.description?.split("\n").map((line, i) => {
                const t = line.trim();
                if (t.startsWith("- ")) return <li key={i}>{t.slice(2)}</li>;
                if (!t) return <br key={i} />;
                return <p key={i}>{t}</p>;
              })}
            </div>

            {product.tags && product.tags.length > 0 && (
              <div className="mt-8 pt-6 border-t border-stone-200">
                <span className="text-sm text-stone-500 ml-2">برچسب‌ها:</span>
                {product.tags.map((t) => (
                  <span key={t} className="inline-block text-xs bg-stone-100 text-stone-700 px-3 py-1 rounded-full ml-2 mb-2">
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
